import classnames from "classnames";
import PropTypes from "prop-types";
import { useComponentInView } from "../../hooks/useComponentInView";
import useOutputReportStore from "../../store/OutputReport.store";
import { reactJsonToJsx } from "../../utils/reactJsonToJsx";
import Definition from "./Definition";
import DefinitionCard from "./DefinitionCard";
import DefinitionListCard from "./DefinitionListCard";

import "./definitions.scss";

const Definitions = ({
  parentSectionKeyKebab,
  heading,
  bodyContent,
  className,
  footnote,
  isReportPage,
  isHeadingHidden,
}) => {
  const { ref } = useComponentInView();

  const metadata = useOutputReportStore().metadata;
  const sectionMetadata = metadata?.Definitions || {};
  const {
    footnotes,
    legalCopy: metadataLegalCopy,
    sectionKeyKebab,
  } = sectionMetadata;

  if (!metadata?.Cover?.data) return null;

  return (
    <section
      ref={ref}
      id={`section-${sectionKeyKebab}${
        parentSectionKeyKebab ? `-${parentSectionKeyKebab}` : ""
      }`}
      className={classnames("definitions", className)}
    >
      {!isHeadingHidden && <h2 className="definitions__heading">{heading}</h2>}
      <div className="definitions__body-container">
        {bodyContent?.map((item, index) => {
          return (
            <div className="definitions__body-item" key={index}>
              <div className="definitions__body-heading-wrapper">
                {item.heading && (
                  <h3 className="definitions__section-heading">
                    {item.heading}
                  </h3>
                )}
                {item.description && (
                  <div className="definitions__section-description">
                    {item.description}
                  </div>
                )}
              </div>
              {item.cardContent && (
                <div className="definitions__card-grid">
                  {item.cardContent.map((card, index) => {
                    return <DefinitionCard key={`card-${index}`} {...card} />;
                  })}
                </div>
              )}
              {item.gridContent && (
                <div className="definitions__grid">
                  {item.gridContent.map((item, index) => {
                    return <Definition key={`definition-${index}`} {...item} />;
                  })}
                </div>
              )}
              {item.content?.map((item, index) => {
                return <Definition key={`definition-${index}`} {...item} />;
              })}
              {item.listContent && (
                <DefinitionListCard
                  key={`definition-${index}`}
                  listContent={item.listContent}
                />
              )}
              {item.note && (
                <div className="definitions__note">
                  <div className="definitions__note-content">{item.note}</div>
                </div>
              )}
              {item.explanation && (
                <div className="definitions__note">
                  <div className="definitions__note-content">
                    {item.explanation}
                  </div>
                </div>
              )}
              {item?.footnoteContent && (
                <div className="definitions__footnotes">
                  <div className="definitions__footnotes-content">
                    {item.footnoteContent}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {footnotes && (
        <div className="definitions__footnote">
          <div className="definitions__footnote-item-content">
            {reactJsonToJsx(footnotes)}
          </div>
          {metadataLegalCopy && (
            <p className="definitions__legal-copy">{metadataLegalCopy}</p>
          )}
        </div>
      )}
    </section>
  );
};

Definitions.propTypes = {
  parentSectionKeyKebab: PropTypes.string,
  heading: PropTypes.string.isRequired,
  bodyContent: PropTypes.arrayOf(
    PropTypes.shape({
      heading: PropTypes.string,
      description: PropTypes.node,
      cardContent: PropTypes.array,
      gridContent: PropTypes.array,
      listContent: PropTypes.array,
      content: PropTypes.array,
      note: PropTypes.string,
      explanation: PropTypes.node,
      footnoteContent: PropTypes.node,
    })
  ),
  className: PropTypes.string,
  footnote: PropTypes.node,
  isReportPage: PropTypes.bool,
  isHeadingHidden: PropTypes.bool,
};

Definitions.defaultProps = {
  heading: "Definitions",
  isReportPage: true,
  isHeadingHidden: false,
};

export default Definitions;
